import React from 'react';

import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { ProgressCircular } from '@americanexpress/dls-react';
import { cn, formatText } from '@/helpers/utils';
import { useRouteParams } from '@/context/routeParamsContext';
import { useTranslatedAirportLounges } from '@/hooks/loungesHooks/loungesHelperHooks';
import {
  useOneCmsLocaleResultsObj,
  useCommonContent,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import RouteReplaceWithQueryParams from '../common/RouteReplaceWithQueryParams';
import CardCalloutNotification from '../common/CardCalloutNotification';
import SearchLoungeResult from './SearchLoungeResult';

const SearchLoungesByAirportList = () => {
  const resultsCopy = useOneCmsLocaleResultsObj();
  const { product } = useRouteParams();
  const { data, error, isLoading } = useTranslatedAirportLounges();
  const {
    pageTitles: { airport },
    productDisplayNames,
  } = useCommonContent();

  const lounges = (data || []).filter(Boolean);
  lounges.sort((a, b) =>
    a.location.terminal.localeCompare(b.location.terminal)
  );

  if (error) {
    return <RouteReplaceWithQueryParams to={`/${product}`} />;
  }

  if (isLoading) {
    return (
      <div className="tw-m-12 tw-flex tw-justify-center">
        <ProgressCircular />
      </div>
    );
  }

  if (lounges.length === 0) {
    return (
      <div className="tw-border-b tw-border-gray-300 tw-p-4">
        <p>Sorry, there are no lounges available for this airport</p>
      </div>
    );
  }

  const { name: airportName, iata } = lounges[0].airport;
  const numCopy =
    lounges.length > 1 ? resultsCopy?.multiple : resultsCopy?.singular;

  // for meta description
  const productName = productDisplayNames[product]?.displayName?.replace(
    'The ',
    ''
  );

  return (
    <>
      <Helmet>
        <title>{formatText(airport?.title, { airportName })}</title>
        <meta
          name="description"
          content={formatText(airport?.description, {
            airportName,
            productName,
          })}
        />
      </Helmet>
      <div className="tw-mb-6 lg_tw-flex">
        <h1 className="tw-heading-lg-book tw-mr-2 tw-text-gray-900 md_tw-heading-display-small-book">
          {formatText(resultsCopy.loungesAt, { airportName, iata })}
        </h1>
        <p className="tw-heading-xs-bold tw-mt-2 tw-flex-shrink-0 tw-text-gray-700 lg_tw-ml-auto lg_tw-mt-auto">
          <FormattedMessage
            id="number-of-lounges"
            defaultMessage={numCopy}
            values={{ length: lounges.length }}
          />
        </p>
      </div>

      <CardCalloutNotification className="tw-mb-6" />

      <style>
        {/* override carousel control button iconButton */}
        {`
          .carousel-override button > :first-child { 
            height: 2.75rem;
          }
        `}
      </style>
      <div className="tw-relative tw-grid tw-grid-cols-1 tw-place-items-center tw-gap-4 sm_tw-grid-cols-2 lg_tw-grid-cols-3 lg_tw-gap-6">
        {lounges.flatMap((lounge, i) => [
          (i === 0 ||
            lounge.location.terminal !== lounges[i - 1].location.terminal) && (
            <h2
              key={lounge.location.terminal}
              className={cn(
                'tw-heading-xs-bold tw-justify-self-start tw-text-gray-700 sm_tw-col-span-2 lg_tw-col-span-3 lg_-tw-mb-2',
                { 'tw-mt-6 lg_tw-mt-4': i > 0 }
              )}
            >
              {lounge.location.terminal}
            </h2>
          ),
          <SearchLoungeResult lounge={lounge} key={lounge.id} />,
        ])}
      </div>
    </>
  );
};

SearchLoungesByAirportList.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

export default SearchLoungesByAirportList;
